import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useDroppable } from '@dnd-kit/core';
import { useGesture } from '@use-gesture/react';
import { AppDispatch, RootState } from 'Vendor/Store/store';
import { GRID_COLUMNS, MAX_ZOOM, MIN_ZOOM, MOBILE_GRID_COLUMNS } from 'Vendor/Utils/Constants';
import { selectActiveLayoutItems, selectSelectedItemIds } from 'Vendor/Store/studio/selectors';
import { itemDeselected, zoomTo } from 'Vendor/Store/studio';
import DraggableGridItem from './Items/_DraggableGridItem';
import { useMarqueeSelection } from 'Hooks/useMarqueeSelection';
import GroupSelectionOverlay from './GroupSelectionOverlay';
import { setGridMetrics, setGroupBounds } from 'Vendor/Store/studio/gridSlice';

interface GridCanvasProps {
    resizePreview: any;
    canvasOffset: { x: number; y: number };
    setCanvasOffset: React.Dispatch<React.SetStateAction<{ x: number; y: number }>>;
}

const GridCanvas: React.FC<GridCanvasProps> = ({ resizePreview, canvasOffset, setCanvasOffset }) => {
    const { setNodeRef } = useDroppable({ id: 'grid-canvas' });
    const dispatch = useDispatch<AppDispatch>();
    const items = useSelector(selectActiveLayoutItems);
    const validItems = useMemo(() => items.filter(Boolean), [items]);
    const responsiveMode = useSelector((state: RootState) => state.studio.present.responsiveMode);
    const zoomLevel = useSelector((state: RootState) => state.studio.present.zoomLevel);
    const isGridVisible = useSelector((state: RootState) => state.studio.present.isGridVisible);
    const [dynamicRowHeight, setDynamicRowHeight] = useState(0);
    const gridRef = useRef<HTMLDivElement>(null);
    const selectedItemIds = useSelector(selectSelectedItemIds);
    const currentGroupBounds = useSelector((state: RootState) => state.studioGrid.groupBounds);
    const currentMetrics = useSelector((state: RootState) => state.studioGrid.gridMetrics);

    const {
        canvasRef: marqueeCanvasRef,
        marquee,
        isMarqueeActive,
        handlePointerDown,
        handlePointerMove,
        handlePointerUp,
        setJustFinished
    } = useMarqueeSelection(validItems);

    const [isPanning, setIsPanning] = useState(false);
    const activePageId = useSelector(
        (state: RootState) => state.studio.present.activePageId,
    )
    const initialZoomRef = useRef(zoomLevel);
    useEffect(() => {
        initialZoomRef.current = zoomLevel;
    }, [zoomLevel]);

    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            const target = e.target as HTMLElement;
            // Prevent panning if an input field is focused
            if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable) {
                return;
            }

            if (e.code === 'Space' && !e.repeat) {
                e.preventDefault();
                setIsPanning(true);
            }
        };
        const handleKeyUp = (e: KeyboardEvent) => {
            if (e.code === 'Space') {
                setIsPanning(false);
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        window.addEventListener('keyup', handleKeyUp);
        return () => {
            window.removeEventListener('keydown', handleKeyDown);
            window.removeEventListener('keyup', handleKeyUp);
        };
    }, []);

    const bind = useGesture(
        {
            // The onDrag handler now updates the parent's state on every frame.
            // This ensures the drop position calculation is always correct.
            onDrag: ({ offset: [x, y] }) => {
                setCanvasOffset({ x, y });
            },
            onWheel: ({ event, ctrlKey, delta: [dx, dy] }) => {
                event.preventDefault();
                if (ctrlKey) {
                    const newZoom = zoomLevel - dy * 0.01;
                    const clampedZoom = Math.max(MIN_ZOOM, Math.min(newZoom, MAX_ZOOM));
                    dispatch(zoomTo(clampedZoom));
                } else {
                    setCanvasOffset((prev) => ({
                        x: prev.x - dx,
                        y: prev.y - dy,
                    }));
                }
            },
            onPinch: ({ offset: [s] }) => {
                const newZoom = initialZoomRef.current * s;
                const clampedZoom = Math.max(MIN_ZOOM, Math.min(newZoom, MAX_ZOOM));
                dispatch(zoomTo(clampedZoom));
            },
            onPinchStart: () => {
                initialZoomRef.current = zoomLevel;
            },
        },
        {
            drag: {
                enabled: isPanning,
                from: () => [canvasOffset.x, canvasOffset.y],
            },
            wheel: {
                eventOptions: { passive: false },
            },
        }
    );

    const activeGridColumns = responsiveMode === 'mobile' ? MOBILE_GRID_COLUMNS : GRID_COLUMNS;

    useEffect(() => {
        const calculateRowHeight = () => {
            if (gridRef.current) {
                const gridWidth = gridRef.current.offsetWidth;
                const columnWidth = gridWidth / activeGridColumns;
                setDynamicRowHeight(columnWidth);
            }
        };

        calculateRowHeight();
        const resizeObserver = new ResizeObserver(calculateRowHeight);
        if (gridRef.current) {
            resizeObserver.observe(gridRef.current);
        }

        return () => {
            if (gridRef.current?.isConnected) {
                resizeObserver.unobserve(gridRef.current);
            }
        };
    }, [responsiveMode, activeGridColumns]);

    const handleCanvasClick = (e: React.MouseEvent) => {
        const target = e.target as HTMLElement;

        if (isMarqueeActive) {
            setJustFinished(false);
            return;
        }

        if (
            target.id === 'grid-canvas' ||
            target.classList.contains('grid-canvas') ||
            !target.closest('.draggable-grid-item')
        ) {
            dispatch(itemDeselected({ pageId: activePageId }));
        }
    };

    const gridStyles = useMemo(() => ({
        display: 'grid',
        gridTemplateColumns: `repeat(${activeGridColumns}, 1fr)`,
        gridAutoRows: `${dynamicRowHeight}px`,
        minHeight: responsiveMode === 'mobile' ? '100vh' : '200vh',
        transform: `translate(${canvasOffset.x}px, ${canvasOffset.y}px) scale(${zoomLevel})`,
        transformOrigin: 'center center',
        touchAction: 'none',
        '--grid-row-height': `${dynamicRowHeight}px`,
        backgroundColor: '#ffffff',
        ...(isGridVisible && {
            backgroundImage: `
            linear-gradient(to right, #e9e9e9 1px, transparent 1px),
            linear-gradient(to bottom, #e9e9e9 1px, transparent 1px)
        `,
            backgroundSize: `calc(100% / ${activeGridColumns}) var(--grid-row-height)`,
        })
    }), [activeGridColumns, dynamicRowHeight, responsiveMode, canvasOffset.x, canvasOffset.y, zoomLevel, isGridVisible]);

    const selectedItems = items.filter((item) => selectedItemIds.includes(item.id));

    const groupBounds = useMemo(() => {
        if (selectedItems.length < 2) return null;

        let left = Infinity, top = Infinity, right = -Infinity, bottom = -Infinity;

        selectedItems.forEach(item => {
            const el = document.querySelector(`[data-id="${item.id}"]`) as HTMLElement;
            if (!el) return;

            const rect = el.getBoundingClientRect();
            const canvasRect = marqueeCanvasRef.current!.getBoundingClientRect();

            const itemLeft = (rect.left - canvasRect.left) / zoomLevel;
            const itemTop = (rect.top - canvasRect.top) / zoomLevel;
            const itemRight = itemLeft + rect.width / zoomLevel;
            const itemBottom = itemTop + rect.height / zoomLevel;

            left = Math.min(left, itemLeft);
            top = Math.min(top, itemTop);
            right = Math.max(right, itemRight);
            bottom = Math.max(bottom, itemBottom);
        });

        return {
            left,
            top,
            width: right - left,
            height: bottom - top,
        };
    }, [selectedItems, marqueeCanvasRef, zoomLevel]);

    useEffect(() => {
        // Only dispatch if bounds actually changed
        if (
            currentGroupBounds === groupBounds ||
            (currentGroupBounds &&
                groupBounds &&
                currentGroupBounds.left === groupBounds.left &&
                currentGroupBounds.top === groupBounds.top &&
                currentGroupBounds.width === groupBounds.width &&
                currentGroupBounds.height === groupBounds.height)
            ) { return; }

            dispatch(setGroupBounds(groupBounds));
        }, [groupBounds, currentGroupBounds, dispatch]);

    useEffect(() => {
        if (!gridRef.current || !dynamicRowHeight) return;

        const gridWidth = gridRef.current.offsetWidth;
        const colWidth = gridWidth / activeGridColumns;
        const rowHeight = dynamicRowHeight;

        // Prevent redundant updates
        if (
            currentMetrics &&
            currentMetrics.colWidth === colWidth &&
            currentMetrics.rowHeight === rowHeight
        ) {
            return; // no change, skip dispatch
        }

        const newMetrics = { colWidth, rowHeight };
        dispatch(setGridMetrics(newMetrics));
    }, [dynamicRowHeight, activeGridColumns, currentMetrics, dispatch]);

    // The gesture is bound to the parent div, which is now outside this component.
    // We just render the canvas itself here.
    return (
        <div
            {...bind()}
            className={`flex-fill p-4 overflow-hidden d-flex justify-content-center align-items-center grid-canvas-outer-container disable-user-select ${responsiveMode} ${isPanning ? 'panning-active' : ''}`}
            style={{ cursor: isPanning ? 'grab' : 'default' }}
            onClick={handleCanvasClick}
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerUp}
        >
            <div
                id="grid-canvas"
                ref={(node) => {
                    setNodeRef(node);
                    // @ts-ignore
                    gridRef.current = node;
                     // @ts-ignore
                    marqueeCanvasRef.current = node;
                }}
                className={`grid-canvas position-relative ${responsiveMode === 'mobile' ? 'mobile-width' : 'w-100'}`}
                style={gridStyles as React.CSSProperties}
            >
                {validItems.map((item) => (
                    <DraggableGridItem
                        key={item?.id}
                        item={item}
                    />
                ))}

                {groupBounds && selectedItems.length > 1 && (
                    <GroupSelectionOverlay
                        bounds={{
                            ...groupBounds,
                            left: groupBounds.left,
                            top: groupBounds.top,
                        }}
                        gridRef={gridRef}
                        dynamicRowHeight={dynamicRowHeight}
                        activeGridColumns={GRID_COLUMNS}
                        selectedItems={selectedItems}
                        zoomLevel={zoomLevel}
                    />
                )}

                {resizePreview && (
                    <div
                        className="resize-preview border border-1"
                        style={{
                            gridColumnStart: resizePreview.position.col_start,
                            gridColumnEnd: `span ${resizePreview.size.col_span}`,
                            gridRowStart: resizePreview.position.row_start,
                            gridRowEnd: `span ${resizePreview.size.row_span}`,
                            zIndex: 1000,
                        }}
                    />
                )}
            </div>
            {marquee.active && (
                <div
                    className="marquee"
                    style={{
                        position: 'fixed',
                        left: Math.min(marquee.startX, marquee.endX),
                        top: Math.min(marquee.startY, marquee.endY),
                        width: Math.abs(marquee.endX - marquee.startX),
                        height: Math.abs(marquee.endY - marquee.startY),
                        border: '1px dashed #666',
                        backgroundColor: 'rgba(0, 120, 215, 0.1)',
                        pointerEvents: 'none',
                        zIndex: 9999,
                    }}
                />
            )}
        </div>
    );
};

export default GridCanvas;

